import { useState, useEffect } from 'react'
import { KeyRound, Plus, Search, Loader2, Info, FolderPlus, Pencil, Trash2, Folder, ChevronDown, ChevronRight, GripVertical } from 'lucide-react'
import { toast } from 'sonner'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { passwordsApi } from '../api'
import { PasswordCard, PasswordForm } from '../components'
import type { Password, PasswordCreateData, PasswordUpdateData, PasswordGroup } from '../types'

// Componente sortable para cada grupo
function SortableGroupCard({
  group,
  passwords,
  isCollapsed,
  onToggle,
  onAddPassword,
  onEditGroup,
  onDeleteGroup,
  onEditPassword,
  onDeletePassword,
}: {
  group: PasswordGroup
  passwords: Password[]
  isCollapsed: boolean
  onToggle: () => void
  onAddPassword: () => void
  onEditGroup: () => void
  onDeleteGroup: () => void
  onEditPassword: (password: Password) => void
  onDeletePassword: (id: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <Card ref={setNodeRef} style={style} className={`border border-gray-300 dark:border-gray-700 transition-colors ${isDragging ? 'opacity-40 border-dashed border-2 border-teal-400 bg-teal-50/30 dark:bg-teal-950/30' : ''}`}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <button
              onClick={onToggle}
              className="flex items-center gap-2 hover:text-foreground transition-colors"
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <Folder className="h-4 w-4 text-teal-500" />
              <CardTitle className="text-base font-semibold">
                {group.nombre}
              </CardTitle>
              <span className="text-sm text-muted-foreground">
                ({passwords.length})
              </span>
            </button>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onAddPassword}
              title="Agregar contraseña"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onEditGroup}
              title="Editar grupo"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={onDeleteGroup}
              title="Eliminar grupo"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="pt-0 pb-3 px-4">
          {passwords.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Sin contraseñas en este grupo
            </p>
          ) : (
            <div className="space-y-2">
              {passwords.map((password) => (
                <PasswordCard
                  key={password.id}
                  password={password}
                  onEdit={onEditPassword}
                  onDelete={onDeletePassword}
                  compact
                />
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

// Preview del grupo al arrastrar
function DragOverlayCard({ group, passwordCount }: { group: PasswordGroup; passwordCount: number }) {
  return (
    <Card className="border-2 border-teal-400 shadow-xl bg-white dark:bg-gray-900 rotate-[2deg] scale-105">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-teal-400" />
          <Folder className="h-4 w-4 text-teal-500" />
          <CardTitle className="text-base font-semibold">
            {group.nombre}
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            ({passwordCount})
          </span>
        </div>
      </CardHeader>
    </Card>
  )
}

export function PasswordsPage() {
  const [passwords, setPasswords] = useState<Password[]>([])
  const [groups, setGroups] = useState<PasswordGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [editingPassword, setEditingPassword] = useState<Password | null>(null)
  const [decryptedPassword, setDecryptedPassword] = useState<string>('')
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [showInfo, setShowInfo] = useState(false)

  // Estado para grupos colapsados
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set())

  // Estado para gestión de grupos
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [editingGroup, setEditingGroup] = useState<PasswordGroup | null>(null)
  const [deleteGroupId, setDeleteGroupId] = useState<number | null>(null)
  const [groupLoading, setGroupLoading] = useState(false)

  // Para crear contraseña directamente en un grupo
  const [createInGroupId, setCreateInGroupId] = useState<number | null>(null)

  // Grupo activamente siendo arrastrado
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null)

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const loadData = async () => {
    try {
      const [passwordsData, groupsData] = await Promise.all([
        passwordsApi.getAll(),
        passwordsApi.getGroups(),
      ])
      setPasswords(passwordsData)
      setGroups(groupsData)
    } catch {
      toast.error('Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const toggleGroup = (groupId: number) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveGroupId(event.active.id as number)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveGroupId(null)
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = groups.findIndex((g) => g.id === active.id)
      const newIndex = groups.findIndex((g) => g.id === over.id)

      const newGroups = arrayMove(groups, oldIndex, newIndex)
      setGroups(newGroups)

      // Guardar el nuevo orden en el backend
      try {
        await passwordsApi.reorderGroups(newGroups.map(g => g.id))
      } catch {
        // Revertir si hay error
        loadData()
        toast.error('Error al reordenar grupos')
      }
    }
  }

  const handleCreate = (groupId?: number) => {
    setEditingPassword(null)
    setDecryptedPassword('')
    setCreateInGroupId(groupId ?? null)
    setFormOpen(true)
  }

  const handleEdit = async (password: Password) => {
    setEditingPassword(password)
    setCreateInGroupId(null)
    try {
      const detail = await passwordsApi.getOne(password.id)
      setDecryptedPassword(detail.password)
      setFormOpen(true)
    } catch {
      toast.error('Error al cargar la contraseña')
    }
  }

  const handleSubmit = async (data: PasswordCreateData | PasswordUpdateData) => {
    setFormLoading(true)
    try {
      if (editingPassword) {
        await passwordsApi.update(editingPassword.id, data)
        toast.success('Contraseña actualizada')
      } else {
        // Si estamos creando en un grupo específico
        if (createInGroupId && !('group_id' in data && data.group_id !== undefined)) {
          (data as PasswordCreateData).group_id = createInGroupId
        }
        await passwordsApi.create(data as PasswordCreateData)
        toast.success('Contraseña creada')
      }
      setFormOpen(false)
      setCreateInGroupId(null)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return

    try {
      await passwordsApi.delete(deleteId)
      toast.success('Contraseña eliminada')
      setDeleteId(null)
      loadData()
    } catch {
      toast.error('Error al eliminar')
    }
  }

  // Funciones para gestión de grupos
  const handleCreateGroup = () => {
    setEditingGroup(null)
    setGroupName('')
    setGroupDialogOpen(true)
  }

  const handleEditGroup = (group: PasswordGroup) => {
    setEditingGroup(group)
    setGroupName(group.nombre)
    setGroupDialogOpen(true)
  }

  const handleSubmitGroup = async () => {
    if (!groupName.trim()) return

    setGroupLoading(true)
    try {
      if (editingGroup) {
        await passwordsApi.updateGroup(editingGroup.id, { nombre: groupName })
        toast.success('Grupo actualizado')
      } else {
        await passwordsApi.createGroup({ nombre: groupName })
        toast.success('Grupo creado')
      }
      setGroupDialogOpen(false)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar grupo')
    } finally {
      setGroupLoading(false)
    }
  }

  const handleDeleteGroup = async () => {
    if (!deleteGroupId) return

    try {
      await passwordsApi.deleteGroup(deleteGroupId)
      toast.success('Grupo eliminado')
      setDeleteGroupId(null)
      loadData()
    } catch {
      toast.error('Error al eliminar grupo')
    }
  }

  // Filtrar contraseñas por búsqueda
  const filteredPasswords = passwords.filter((p) =>
    p.nombre.toLowerCase().includes(search.toLowerCase()) ||
    p.username.toLowerCase().includes(search.toLowerCase()) ||
    p.url.toLowerCase().includes(search.toLowerCase())
  )

  // Agrupar contraseñas
  const passwordsByGroup = groups.map(group => ({
    group,
    passwords: filteredPasswords.filter(p => p.group_id === group.id)
  }))

  const ungroupedPasswords = filteredPasswords.filter(p => p.group_id === null)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-black dark:text-white" />
            Contraseñas
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Guarda tus credenciales de servicios externos de forma segura
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCreateGroup}
            variant="outline"
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            Nuevo Grupo
          </Button>
          <Button
            onClick={() => handleCreate()}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva
          </Button>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, usuario o URL..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      ) : filteredPasswords.length === 0 && groups.length === 0 ? (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="py-12 text-center">
            <KeyRound className="h-12 w-12 mx-auto text-teal-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Sin contraseñas</h3>
            <p className="text-muted-foreground mb-4">
              Crea tu primera contraseña para empezar
            </p>
            <Button
              onClick={() => handleCreate()}
              className="bg-teal-800 hover:bg-teal-900 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Contraseña
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Grupos con drag and drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={groups.map(g => g.id)}
              strategy={verticalListSortingStrategy}
            >
              {passwordsByGroup.map(({ group, passwords: groupPasswords }) => (
                <SortableGroupCard
                  key={group.id}
                  group={group}
                  passwords={groupPasswords}
                  isCollapsed={collapsedGroups.has(group.id)}
                  onToggle={() => toggleGroup(group.id)}
                  onAddPassword={() => handleCreate(group.id)}
                  onEditGroup={() => handleEditGroup(group)}
                  onDeleteGroup={() => setDeleteGroupId(group.id)}
                  onEditPassword={handleEdit}
                  onDeletePassword={setDeleteId}
                />
              ))}
            </SortableContext>
            <DragOverlay dropAnimation={{
              duration: 200,
              easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
            }}>
              {activeGroupId ? (() => {
                const item = passwordsByGroup.find(({ group }) => group.id === activeGroupId)
                return item ? (
                  <DragOverlayCard
                    group={item.group}
                    passwordCount={item.passwords.length}
                  />
                ) : null
              })() : null}
            </DragOverlay>
          </DndContext>

          {/* Contraseñas sin grupo */}
          {ungroupedPasswords.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleGroup(0)}
                    className="flex items-center gap-2 hover:text-foreground transition-colors"
                  >
                    {collapsedGroups.has(0) ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <CardTitle className="text-base font-semibold text-muted-foreground">
                      Sin grupo
                    </CardTitle>
                    <span className="text-sm text-muted-foreground">
                      ({ungroupedPasswords.length})
                    </span>
                  </button>
                </div>
              </CardHeader>
              {!collapsedGroups.has(0) && (
                <CardContent className="pt-0 pb-3 px-4">
                  <div className="space-y-2">
                    {ungroupedPasswords.map((password) => (
                      <PasswordCard
                        key={password.id}
                        password={password}
                        onEdit={handleEdit}
                        onDelete={setDeleteId}
                        compact
                      />
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

      <PasswordForm
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setCreateInGroupId(null)
        }}
        password={editingPassword}
        decryptedPassword={decryptedPassword}
        groups={groups}
        defaultGroupId={createInGroupId}
        onSubmit={handleSubmit}
        loading={formLoading}
      />

      {/* Dialog eliminar contraseña */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar contraseña</DialogTitle>
            <DialogDescription>
              Esta accion no se puede deshacer. Se eliminara la contraseña permanentemente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog crear/editar grupo */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Input
                placeholder="Nombre del grupo"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitGroup}
              className="bg-teal-800 hover:bg-teal-900 text-white"
              disabled={groupLoading || !groupName.trim()}
            >
              {groupLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingGroup ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog eliminar grupo */}
      <Dialog open={!!deleteGroupId} onOpenChange={() => setDeleteGroupId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar grupo</DialogTitle>
            <DialogDescription>
              Se eliminará el grupo. Las contraseñas del grupo quedarán sin grupo asignado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteGroupId(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
